import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography,
  Box,
  Paper,
  Chip,
  Grid,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  useTheme,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { EnhancedTable } from "../common/EnhancedTable";
import EditLicenseDialog from "../dialogs/EditLicenseDialog";



function TabPanel({ children, value, index }) {
  return value === index && <Box sx={{ pt: 3 }}>{children}</Box>;
}

export default function LicenseDetails({ authData }) {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const { id } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasUpdatePermission = authData?.permission?.includes('update-license');

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };


  useEffect(() => {
    axios
      .get(`/api/licenses/${id}`)
      .then((response) => {
        setLicense(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching license details:", error);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!license) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h5" color="error">
          License not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>

      {/* Main Content */}
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: 'text.primary' }}
          onClick={() => navigate("/")}
        >
          Back to search
        </Button>

        {/* License Header */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <Chip
            label="License"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              borderRadius: 1,
            }}
          />
          <Typography sx={{ color: 'black' }}>
            {license.id}
          </Typography>
          <Chip
            label={license.status}
            sx={{
              bgcolor: '#FFE082',
              color: 'text.primary',
              fontWeight: 'bold',
            }}
          />
        </Box>

        {/* License Info Card */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography>{license.startsAt}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">End Date</Typography>
                <Typography>{license.endsAt}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography>{license.duration}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography>{license.status}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">License Type</Typography>
                <Typography>{license.type}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Entitlement Type</Typography>
                <Typography>{license.entitlementType}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Redeemed Entitlements</Typography>
                <Typography>{license.numEntitlements}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Max Entitlements</Typography>
                <Typography>{license.maxEntitlements}</Typography>
              </Box>
              <Box sx={{ mt: 2}}>
                <Typography variant="caption" color="text.secondary">Remaining Entitlements</Typography>
                <Typography>{license.maxEntitlements - license.numEntitlements}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                <Typography>{'Coming soon...'}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Owner Username</Typography>
                <Typography>{'Coming soon...'}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Owner Email</Typography>
                <Typography>{'Coming soon...'}</Typography>
              </Box>
              <Box sx = {{ mt : 2}}>
                <Typography variant="caption" color="text.secondary">Auto Renew</Typography>
                <Typography>{license.autoRenew ? license.autoRenew : "N/A"}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs and Content */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Additional Detail" />
            <Tab label="History" />
            <Tab label="Redemption History" />
            <Tab label="Products" />
            <Tab label="Personnel" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Table>
            <TableBody>
                <TableRow>
                    <TableCell sx={{ width: '20%' }}>Order ID:</TableCell>
                    <TableCell sx = {{ width: '80%' }}>{license.orderId}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell sx={{ width: '20%' }}>Order System:</TableCell>
                    <TableCell sx={{ width: '80%' }}>{license.orderSystem}</TableCell>
                </TableRow>
            </TableBody>
          </Table>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
            {/* History Tab */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="h6" color="text.secondary">
                Coming Soon...
                </Typography>
            </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
            {/* Redemption History Tab */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="h6" color="text.secondary">
                Coming Soon...
                </Typography>
            </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
            {/* Products Tab */}
            <EnhancedTable
                headCells={[
                { id: "productName", label: "Product Name" },
                { id: "platform", label: "Platform" },
                { id: "productModel", label: "Product Model" },
                { id: "isbn", label: "ISBN" },
                ]}
                order="asc"
                orderBy="productName"
                count={1} // Only one row for now
                page={0}
                rowsPerPage={1}
                rowsPerPageOptions={[1]} // Fixed rows per page
                savePaginationState={() => {}} // No pagination state needed for now
            >
                <TableBody>
                <TableRow>
                    <TableCell>Coming Soon</TableCell>
                    <TableCell>Coming Soon</TableCell>
                    <TableCell>Coming Soon</TableCell>
                    <TableCell>Coming Soon</TableCell>
                </TableRow>
                </TableBody>
            </EnhancedTable>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
            {/* Personnel Tab */}
            <Table>
                <TableHead>
                <TableRow>
                    <TableCell  
                    sx={{
                        width: '50%',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        bgcolor: 'background.secondary',
                        color: 'black',
                        borderRight: '1px solid #ddd',
                    }}>
                    Administrators
                    </TableCell>
                    <TableCell
                    sx={{
                        width: '50%',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        bgcolor: 'background.secondary',
                        color: 'black',
                    }}>
                    Contacts
                    </TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                <TableRow>
                    <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ddd', }}>
                    Coming Soon
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                    Coming Soon
                    </TableCell>
                </TableRow>
                </TableBody>
            </Table>
        </TabPanel>

        {/* Edit License Button */}
        {hasUpdatePermission ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                borderRadius: 1,
              }}
              onClick={handleDialogOpen}
            >
              Edit License
            </Button>
          </Box>
        ) : ''}
      </Box>
      <EditLicenseDialog open={dialogOpen} onClose={handleDialogClose} />
    </Box>
  );
}